// FULLCALENDAR naptár-wrapper — hónap / hét / nap / lista nézet, ÁTÜTEMEZÉS DRAG & DROP-pal.
//
// MIÉRT FULLCALENDAR: a legacy naptár PONTOSAN ezt használta (core + daygrid + timegrid + list + interaction),
// és a drag & drop az `interaction` pluginnal MIT-licencű, beépített funkció. A korábbi Schedule-X core-ban a
// DnD FIZETŐS kiegészítő — emiatt maradt ki az átütemezés, ami a legacy naptár egyik fő munkafolyamata volt.
// Ráadásul a FullCalendar szállít MAGYAR locale-t, így a korábbi kézi fordítás-tábla is elhagyható.
//
// Legacy-parity a naptár-beállításokban: `slotMinTime 06:00` · `slotMaxTime 22:00` · nincs egész-napos sáv ·
// a végidő is látszik az eseményen · hétfő-kezdet magyarul · hétvége-elrejtés kapcsoló (a választás megőrizve).
//
// Adat-kötés TARTOMÁNY-alapú: a látható tartomány változásakor (`datesSet`) a szülő tölt (BFF from/to), az
// eredményt `events` propként kapjuk. A `timeZone` `Europe/Budapest` — a busz UTC-ben tárol.
import { type EventInput } from '@fullcalendar/core';
import enLocale from '@fullcalendar/core/locales/en-gb';
import huLocale from '@fullcalendar/core/locales/hu';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BUSINESS_END_HOUR, BUSINESS_START_HOUR } from '../business-hours';
import { calendarDateToUtcIso, calendarNow, peekAnchorFor, rangeToIso } from '../calendar-map';

import { useIsMobile } from '@/hooks/use-is-mobile';

import '@/styles/meetings-calendar-theme.css';

/** Az átütemezés (drag/resize) kérése — a szülő megerősítést kér, majd ment; elutasításnál `revert()`. */
export interface RescheduleRequest {
  id: string;
  /** Az ÚJ kezdet/vég UTC ISO-ban (a naptár a megjelenítési zónából számolja). */
  startsAt: string;
  endsAt: string;
  /** A naptár visszaállítója — megerősítés-elutasításnál VAGY szerver-hibánál KÖTELEZŐ meghívni. */
  revert: () => void;
}

export interface MeetingsCalendarProps {
  /** A látható tartományra leképezett események (a szülő tölti tartomány-alapon). */
  events: EventInput[];
  /** A látható tartomány változott (nézet-/dátum-váltás) — a szülő ebből tölt (UTC ISO from/to). */
  onRangeChange: (from: string, to: string) => void;
  /**
   * Eseményre kattintás — a MeetingRow id-je ÉS a kattintott esemény-elem téglalapja (viewport-koordináták).
   * A téglalap a részlet-kártya („peek") HORGONYA: a kártya ott nyílik, ahol a felhasználó kattintott.
   */
  onEventClick: (
    id: string,
    anchor: { top: number; left: number; width: number; height: number },
  ) => void;
  /**
   * Üres nap/idősáv kattintás — a HELYI kezdő-időpont ('YYYY-MM-DDTHH:mm') és hogy NAP-kattintás volt-e
   * (`allDay`: hónap-nézetben a cella 00:00-t ad, ott a hívó munkaidő-kezdést tölt elő). Opcionális create.
   */
  onSelectSlot?: ((localStart: string, allDay: boolean) => void) | undefined;
  /** Átütemezés drag/resize után. Nincs megadva → a naptár NEM szerkeszthető (jog-hiány). */
  onReschedule?: ((req: RescheduleRequest) => void) | undefined;
  /** A megjelenítendő nyelv (i18n) — `hu` → magyar locale, egyébként brit angol (hétfő-kezdet, 24 órás óra). */
  locale: string;
  /** A hétvége-kapcsoló feliratai (i18n-ből: „hétvége el/be") — a lib nem ismeri az app fordítás-rétegét. */
  weekendToggleLabels: {
    /** A gomb FELIRATA (egy szó: „hétvége") — az állapotot a szem-ikon jelzi. */
    label: string;
    /** Tooltip/a11y-cím a KÖVETKEZŐ művelethez („hétvége el" / „hétvége be"). */
    hide: string;
    show: string;
  };
  /**
   * A naptár-gombok feliratai (i18n-ből, KISBETŰVEL): `ma` · `hónap` · `hét` · `nap` · `lista`.
   * MIÉRT ÍRJUK FELÜL a lib locale-ját: a FullCalendar magyar/angol locale-je nagy kezdőbetűs feliratokat ad
   * („Hónap", „Ma"), az app konvenciója viszont a választó-/érték-feliratokra kisbetűs (lásd `design.md`).
   */
  buttonLabels: { today: string; month: string; week: string; day: string };
  /**
   * Az INDULÓ nap (`YYYY-MM-DD`) — a deep-link ezzel ugrik a megbeszélés napjára (a naptár a látható tartományt
   * tölti, tehát egy másik hónapra eső megbeszélés enélkül nem is töltődne be). Csak induláskor hat.
   */
  initialDate?: string | undefined;
}

/** A hétvége-elrejtés választásának megőrzése (a legacy `localStorage`-alapú kapcsolójának párja). */
const WEEKEND_STORAGE_KEY = 'atlas.meetings.calendar.showWeekends';

function readShowWeekends(): boolean {
  try {
    return window.localStorage.getItem(WEEKEND_STORAGE_KEY) !== 'false';
  } catch {
    // Privát mód / letiltott tároló → a hétvége LÁTSZIK (a szélesebb nézet a biztonságos alapérték).
    return true;
  }
}

/**
 * A naptárból kapott `Date` → 'YYYY-MM-DDTHH:mm' MEGJELENÍTÉSI-zónás input-string (a create-form előtöltése).
 * A naptár `timeZone: 'UTC'`-vel fut, és a `calendar-map` már a budapesti fal-időt adta át — tehát a `Date`
 * UTC-MEZŐI MAGUK a fal-idő. (Korábban itt egy `toLocaleString(..., timeZone)` konverzió volt, ami a már
 * shiftelt értéket MÁSODSZOR is zónázta volna.)
 */
function toLocalInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${String(date.getUTCFullYear())}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
  );
}

export function MeetingsCalendar({
  events,
  onRangeChange,
  onEventClick,
  onSelectSlot,
  onReschedule,
  locale,
  weekendToggleLabels,
  buttonLabels,
  initialDate,
}: MeetingsCalendarProps): JSX.Element {
  const [showWeekends, setShowWeekends] = useState<boolean>(readShowWeekends);
  /**
   * MOBIL-fejléc (a legacy `isMobile`-jának párja): telefonon a hónap-nézet olvashatatlan és a négy
   * nézet-gomb kicsordul, ezért ott a LISTA + NAP nézet marad, „ma" nélkül. A küszöb a KÖZÖS
   * `useIsMobile`-ból jön (a kampány-előrejelzés is ezt használja — egy küszöb, nem kettő).
   */
  const isMobile = useIsMobile();

  // A friss prop-függvények ref-en át: a FullCalendar a callbackeket a render-ciklustól függetlenül hívja,
  // és így egy prop-változás nem igényel naptár-újraépítést.
  const rangeRef = useRef(onRangeChange);
  const clickRef = useRef(onEventClick);
  const slotRef = useRef(onSelectSlot);
  const rescheduleRef = useRef(onReschedule);
  rangeRef.current = onRangeChange;
  clickRef.current = onEventClick;
  slotRef.current = onSelectSlot;
  rescheduleRef.current = onReschedule;

  useEffect(() => {
    try {
      window.localStorage.setItem(WEEKEND_STORAGE_KEY, showWeekends ? 'true' : 'false');
    } catch {
      // A tároló-hiba nem befolyásolja a nézetet (a választás csak nem őrződik meg).
    }
  }, [showWeekends]);

  const localeConfig = useMemo(() => (locale.startsWith('hu') ? huLocale : enLocale), [locale]);

  /** A drag/resize közös feldolgozója: az ÚJ időpontok + a naptár `revert`-je a szülőhöz. */
  const handleChange = useCallback(
    (info: { event: { id: string; start: Date | null; end: Date | null }; revert: () => void }) => {
      const start = info.event.start;
      if (!start) {
        info.revert();
        return;
      }
      // A vég hiányzik (nulla hosszú esemény) → a kezdettel egyezik (a busz `endsAt`-ja kötelező).
      const end = info.event.end ?? start;
      const handler = rescheduleRef.current;
      if (!handler) {
        info.revert();
        return;
      }
      handler({
        id: info.event.id,
        // A naptár „fake-UTC" Date-jei a budapesti fal-időt hordozzák → valódi instantra fordítva
        // (DST-tudatosan, a zóna-szabályok szerint — nem fix offsettel).
        startsAt: calendarDateToUtcIso(start),
        endsAt: calendarDateToUtcIso(end),
        revert: info.revert,
      });
    },
    [],
  );

  return (
    <div className={`fc-atlas h-full min-h-0 ${showWeekends ? 'weekends-on' : 'weekends-off'}`}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
        {...(initialDate === undefined ? {} : { initialDate })}
        headerToolbar={
          isMobile
            ? { left: 'prev,next', center: 'title', right: 'listWeek,timeGridDay' }
            : {
                left: 'prev,next today weekendToggle',
                center: 'title',
                // DESKTOP: NINCS lista-nézet (user-döntés) — a heti rács ott a jobb eszköz, a
                // listás alak a MOBIL nézet erőssége. Így a szegmens-vezérlő is három gombra egyszerűsödik.
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }
        }
        locale={localeConfig}
        // A lib locale-jének nagy kezdőbetűs feliratai helyett az app kisbetűs kulcsai.
        buttonText={{
          today: buttonLabels.today,
          month: buttonLabels.month,
          week: buttonLabels.week,
          day: buttonLabels.day,
          // A `listWeek` CSAK MOBILON jelenik meg, és az is HETI nézet (csak listás alakban) → a felirata
          // „hét", nem „lista". A „lista" szó a felhasználónak nem nézet-hossz, hanem forma — mobilon
          // félrevezető lett volna két gomb közül („lista" vs „nap") kiválasztani a hetet.
          list: buttonLabels.week,
        }}
        // A HÓNAP-nézetben is TELJES időpont: `6:00 - 7:00` (a lib defaultja az egész órát „6"-ra rövidíti,
        // ami egy naptárban félreérthető — a felhasználó nem tudja, perc-pontos-e).
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          omitZeroMinute: false,
        }}
        // ⚠️ `UTC` — NEM tévedés: a `timeZone: 'Europe/Budapest'` PLUGIN NÉLKÜL csendben UTC-ként viselkedik
        // (a FullCalendar nevesített zónához `@fullcalendar/luxon3`-at kér), és ez adta a 2 órás eltérést a
        // naptár és a szerkesztő-form között. Ehelyett a `calendar-map` MÁR a megjelenítési zóna fal-idejét
        // adja át (naiv ISO), a naptár pedig azt UTC-ként rajzolja ki → a rács a budapesti időt mutatja.
        // A visszaút (drag/resize, slot-kattintás) a `calendarDateToUtcIso`-val megy igazi UTC-be.
        timeZone="UTC"
        // A NÉZET-FELÜLET ugyanazt az osztályt kapja, mint a lista-oldalak tábla-doboza (`card-glass-lg`) —
        // nem egy CSS-másolatot: sötétben tömör near-black (#161618), világosban fehér, ugyanaz a hairline és
        // lift. MIÉRT a lib opciója és nem a `.fc-atlas` wrapper: a wrapper a FEJLÉCET is tartalmazza, ott a
        // felület beolvasztaná a gombok saját hátterét. A `viewClassNames` pontosan a nézet GYÖKÉR-elemére
        // tesz osztályt — egy helyen, MIND a négy nézetre (hónap/hét/nap/lista).
        viewClassNames="card-glass-lg overflow-hidden rounded-xl"
        height="100%"
        // Legacy-parity: 06:00–22:00 sávok, nincs egész-napos sor, a végidő is látszik. A két sáv-határ a
        // KÖZÖS munkaidő-konstansokból jön (`business-hours.ts`) — ugyanaz az ablak, amit a form előtölt és
        // validál; külön szám itt azt jelentené, hogy a naptár olyan időpontot ajánl, amit a mentés visszadob.
        slotMinTime={`${String(BUSINESS_START_HOUR).padStart(2, '0')}:00:00`}
        slotMaxTime={`${String(BUSINESS_END_HOUR).padStart(2, '0')}:00:00`}
        allDaySlot={false}
        displayEventEnd
        nowIndicator
        // a „most" (a vonal + a mai nap) a BUDAPESTI fal-idő — a naptár UTC-n fut, lásd `calendarNow`
        now={() => calendarNow()}
        weekends={showWeekends}
        // HÓNAP-NÉZET SŰRŰSÉG: cellánként legfeljebb KETTŐ megbeszélés látszik, a többi a „+X további" mögé
        // kerül (user-döntés). MIÉRT 2: a cella-magasság így fix és MINDEN sorban egyforma — a
        // korábbi 3-nál a sűrű napok „kinyomták" a rácsot, a ritkák pedig alacsonyak maradtak, tehát a hónap
        // rácsa hullámzott. A fix magasságot a téma-CSS `min-height`-je adja (a nap-szám + 2 pill + a
        // „+X további" helye), az `expandRows` pedig a maradék helyet EGYENLŐEN osztja szét a sorok között.
        dayMaxEvents={2}
        expandRows
        // HÉT- ÉS NAP-NÉZET: a sorok NEM töltik ki a képernyőt — a magasság FIX és az időtartammal arányos
        // (15 perc = egy olvasható sor; a téma-CSS `.fc-timegrid-slot` szabálya), a nap görgethető. A kitöltéssel a
        // 06–22 sáv egy képernyőre préselődött, és a 15 perces megbeszélés ~10 px-re zsugorodott (olvashatatlan).
        // A rövid esemény (az `eventShortHeight` alatt) EGY sorban mutatja az időt és a címet.
        views={{ timeGridWeek: { expandRows: false }, timeGridDay: { expandRows: false } }}
        events={events}
        // SZERKESZTHETŐSÉG = jog: átütemező-callback nélkül (nincs `meetings.manage`) a naptár nem draggelhető.
        editable={onReschedule !== undefined}
        eventStartEditable={onReschedule !== undefined}
        eventDurationEditable={onReschedule !== undefined}
        selectable={onSelectSlot !== undefined}
        datesSet={(arg) => {
          const { from, to } = rangeToIso({ start: arg.start, end: arg.end });
          rangeRef.current(from, to);
        }}
        eventClick={(arg) => {
          arg.jsEvent.preventDefault();
          // A KATTINTOTT elem téglalapja a részlet-kártya horgonya (a kártya ott nyílik, ahol a felhasználó
          // kattintott); a kártyánál SZÉLESEBB eseménynél a kattintás pontja (`peekAnchorFor`). A hónap-nézet
          // „+X további" popoverjéből is működik: ott is az esemény-elem a forrás.
          clickRef.current(
            arg.event.id,
            peekAnchorFor(arg.el.getBoundingClientRect(), arg.jsEvent.clientX),
          );
        }}
        dateClick={(arg) => slotRef.current?.(toLocalInput(arg.date), arg.allDay)}
        eventDrop={handleChange}
        eventResize={handleChange}
        customButtons={{
          weekendToggle: {
            // A legacy „hétvége" kapcsolója: a hétvégi napok elrejtése/megjelenítése, megőrzött választással.
            // A FELIRAT EGYETLEN szó („hétvége") — az ÁLLAPOTOT a szem-ikon mondja el (CSS-maszk, a wrapper
            // `weekends-on/off` osztálya szerint). A művelet iránya a `hint`-ben (tooltip + a11y-cím) marad,
            // mert az ikon önmagában nem olvasható fel: „hétvége el" / „hétvége be".
            text: weekendToggleLabels.label,
            hint: showWeekends ? weekendToggleLabels.hide : weekendToggleLabels.show,
            click: () => setShowWeekends((v) => !v),
          },
        }}
      />
    </div>
  );
}
